import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Bell, LogOut, Menu, X, Home, User, FileText, Bookmark, BellRing, Crown, Briefcase, Users, CreditCard, Building2, GraduationCap, CalendarDays, Trophy, Shield, ListChecks, Flag, Tags, IndianRupee, ScrollText, Mail, LayoutDashboard, FileSpreadsheet, Wallet, Gift, Settings } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Seo } from "@/components/Seo";
import { useAuth, HOME_BY_ROLE } from "@/context/AuthContext";
import { api, timeAgo } from "@/lib/api";
import { toast } from "sonner";

const MENUS = {
  seeker: [["Dashboard", "/dashboard", Home], ["Profile & Resume", "/profile", User], ["Applications", "/applications", FileText], ["Saved Jobs", "/saved-jobs", Bookmark], ["Job Alerts", "/alerts", BellRing], ["Membership", "/membership", Crown], ["Referrals", "/referrals", Gift]],
  employer: [["Dashboard", "/employer/dashboard", LayoutDashboard], ["Jobs", "/employer/jobs", Briefcase], ["Applicants", "/employer/applications", Users], ["Billing", "/employer/billing", CreditCard], ["Referrals", "/employer/referrals", Gift]],
  college: [["Dashboard", "/college/dashboard", Building2], ["Students", "/college/students", GraduationCap], ["Campus Drives", "/college/drives", CalendarDays], ["Placements", "/college/placements", Trophy]],
  admin: [["Overview", "/admin", Shield], ["Users", "/admin/users", Users], ["Excel Reports", "/admin/data", FileSpreadsheet], ["Verifications", "/admin/verifications", Building2], ["Jobs", "/admin/moderation", ListChecks], ["Reports", "/admin/reports", Flag], ["Categories", "/admin/categories", Tags], ["Salary Ranges", "/admin/salaries", Wallet], ["Pricing", "/admin/pricing", Crown], ["Payments", "/admin/payments", IndianRupee], ["Referrals", "/admin/referrals", Gift], ["Settings", "/admin/settings", Settings], ["Audit Logs", "/admin/audit", ScrollText], ["Email Log", "/admin/emails", Mail]],
};

export function RequireRole({ roles }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="grid min-h-screen place-items-center text-sm text-slate-500" data-testid="auth-loading">Loading…</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (!roles.includes(user.role)) return <Navigate to={HOME_BY_ROLE[user.role]} replace />;
  return <DashboardLayout />;
}

function NotificationBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  const toggle = async () => {
    setOpen(!open);
    if (!open && data.unread) {
      await api.post("/notifications/read-all");
      setData((d) => ({ ...d, unread: 0 }));
    }
  };
  return (
    <div className="relative">
      <button onClick={toggle} className="relative rounded-lg p-2 hover:bg-[#EAF8EF]" data-testid="notifications-bell" aria-label="Notifications">
        <Bell size={20} />
        {data.unread > 0 && <span className="absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-[#00B94F] px-1 text-[10px] font-bold text-white" data-testid="notifications-unread">{data.unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-[calc(100vw-2rem)] max-w-xs overflow-hidden rounded-xl border border-[#e3ece6] bg-white shadow-xl" data-testid="notifications-panel">
          <div className="border-b px-4 py-3 text-sm font-semibold">Notifications</div>
          <div className="max-h-96 overflow-y-auto">
            {data.items.length === 0 && <p className="p-4 text-sm text-slate-500">You're all caught up.</p>}
            {data.items.map((n) => (
              <Link key={n.id} to={n.link || "#"} onClick={() => setOpen(false)} className="block border-b border-slate-50 px-4 py-3 hover:bg-[#f3fbf6]">
                <p className="text-sm font-medium">{n.title}</p>
                <p className="mt-0.5 line-clamp-2 text-xs text-slate-500">{n.body}</p>
                <p className="mt-1 text-[10px] text-slate-400">{timeAgo(n.created_at)}</p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function VerifyBanner({ user }) {
  if (user.email_verified) return null;
  const resend = async () => {
    try {
      await api.post("/auth/resend-verification");
      toast.success("Verification email sent");
    } catch (e) {
      toast.error("Please wait before requesting another email");
    }
  };
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="verify-email-banner">
      <span>Please verify your email <b>{user.email}</b> to secure your account.</span>
      <button className="btn-outline btn-sm" onClick={resend} data-testid="resend-verification-btn">Resend email</button>
    </div>
  );
}

function DashboardLayout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const items = MENUS[user.role];
  const side = (
    <nav className="flex flex-col gap-1">
      {items.map(([label, to, Icon]) => (
        <NavLink key={to} to={to} end={to === "/admin"} onClick={() => setOpen(false)} data-testid={`side-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[#00B94F] text-white" : "text-white/75 hover:bg-white/10 hover:text-white"}`}>
          <Icon size={17} />{label}
        </NavLink>
      ))}
    </nav>
  );
  return (
    <div className="flex min-h-screen bg-[#f6faf7]">
      <Seo title="Dashboard" noindex />
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 transform overflow-y-auto bg-[#0B1B2B] p-5 transition-transform lg:static lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="mb-8 flex items-center justify-between"><Logo dark /><button className="text-white lg:hidden" onClick={() => setOpen(false)}><X /></button></div>
        <p className="mb-3 px-3 text-[10px] font-semibold uppercase tracking-widest text-white/40">{{ seeker: "Job Seeker", employer: "Employer", college: "Placement Cell", admin: "Admin" }[user.role]}</p>
        {side}
        <Link to="/jobs" className="mt-8 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-white/60 hover:text-white"><Briefcase size={17} />Browse jobs</Link>
      </aside>
      {open && <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-[#e3ece6] bg-white px-4 sm:px-8">
          <div className="flex items-center gap-3 lg:hidden"><button onClick={() => setOpen(true)} data-testid="dash-menu-toggle" aria-label="Open menu"><Menu /></button><Link to="/" className="text-lg font-extrabold">Job<span className="text-[#00B94F]">fix</span>now</Link></div>
          <div className="hidden text-sm text-slate-500 lg:block">Welcome back, <span className="font-semibold text-[#0B1B2B]" data-testid="dash-user-name">{user.name}</span>{user.is_plus && <span className="ml-2 chip bg-[#0B1B2B] text-white">PLUS</span>}</div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <button className="btn-ghost !px-3" data-testid="dash-logout-btn" aria-label="Logout" onClick={async () => { await logout(); nav("/"); }}><LogOut size={16} /><span className="hidden sm:inline">Logout</span></button>
          </div>
        </header>
        <main className="flex-1 p-4 pb-24 sm:p-8 lg:pb-8"><VerifyBanner user={user} /><Outlet /></main>
      </div>
      <nav className="pb-safe fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 border-t border-[#e3ece6] bg-white lg:hidden" data-testid="mobile-bottom-nav">
        {items.slice(0, 4).map(([label, to, Icon]) => (
          <NavLink key={to} to={to} end={to === "/admin"} data-testid={`bottom-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2 text-[10px] font-medium ${isActive ? "text-[#00B94F]" : "text-slate-500"}`}>
            <Icon size={20} /><span className="max-w-full truncate px-1">{label.split(" ")[0]}</span>
          </NavLink>
        ))}
        <button onClick={() => setOpen(true)} className="flex flex-col items-center gap-0.5 py-2 text-[10px] font-medium text-slate-500" data-testid="bottom-more"><Menu size={20} />More</button>
      </nav>
    </div>
  );
}
